import type { SapUi5RootView } from '@sap/ux-specification-types';
import {
    v4,
    FIORI_FCL_ROOT_ID,
    FIORI_FCL_ROOT_VIEW_NAME,
    ViewTypes,
    FlexibleColumnLayoutAggregations
} from '@sap/ux-specification-types';
import type { ApplicationV4 } from '../application';
import { Constants as V4Constants } from './manifest';
import { updateDependencyLib } from './utils';

// Aggregations mapping for target levels
// 0 - BeginColumnPages, 1 - MidColumnPages, 2 - EndColumnPages
const fclAggregations = [
    FlexibleColumnLayoutAggregations.BeginColumnPages,
    FlexibleColumnLayoutAggregations.MidColumnPages,
    FlexibleColumnLayoutAggregations.EndColumnPages
];

/**
 * Recursive method which updates manifest routing to enable FCL support.
 * Method contains some parts like setting 'ThreeColumnsEndExpanded' to 2st->3rd page navigation - it was requested for demo purposes.
 *
 * @param manifestUI5 - SAPUI5 manifest data.
 * @param app - Project application.
 * @param pageName - Page name which should be used to apply FCL.
 * @param targetPages - Current array of target pages to join in.
 * For example to display two column FCL we have - ['1stLevelPage', '2ndLevelPage']
 * @param {number} level - Current navigation level.
 */
const applyFCLRouting = (
    manifestUI5: v4.SapUi5V4,
    app: ApplicationV4,
    pageName: string,
    targetPages: Array<string> = [],
    level = 0
): void => {
    const routeOfPage = manifestUI5.routing.routes.find((route) => {
        return route.name === pageName;
    });
    if (!routeOfPage) {
        return;
    }
    // Update route
    if (typeof routeOfPage.target === 'string') {
        // 1-3 level navigation is placed like [`1st Page`, `2nd Page`, `3rd Page`]
        // 4+ level navigation is placed as fullscreen - [`4th+n Page`]
        routeOfPage.target = level <= 2 ? [...targetPages, routeOfPage.target] : [routeOfPage.target];
    }
    // Update target
    const target = manifestUI5.routing.targets[pageName];
    const page = app.pages?.[pageName];
    if (!page) {
        return;
    }
    if ('controlAggregation' in page) {
        target.controlAggregation = page.controlAggregation;
    } else if (fclAggregations[level]) {
        target.controlAggregation = fclAggregations[level];
    } else {
        target.controlAggregation = FlexibleColumnLayoutAggregations.EndColumnPages;
    }
    const pattern = routeOfPage.pattern.replace(V4Constants.OptionalQuery, '');
    if (pattern) {
        target.contextPattern = pattern.startsWith('/') ? pattern : `/${pattern}`;
    } else {
        target.contextPattern = '';
    }
    // Loop further pages
    if (page.navigation) {
        level++;
        for (const pageId in page.navigation) {
            applyFCLRouting(manifestUI5, app, page.navigation[pageId]['route'], routeOfPage.target, level);
        }
    }
};

/**
 * Method enables FCL support by updating Manifest.
 *
 * @param manifestUI5 - SAPUI5 manifest data.
 * @param app - Project application.
 */
const enableFcl = (manifestUI5: v4.SapUi5V4, app: ApplicationV4): void => {
    // Apply config with router class
    manifestUI5.routing.config.routerClass = v4.FIORI_FCL_ROUTER_CLASS;
    // Make changes in
    applyFCLRouting(manifestUI5, app, app.home);
    // Update 'rootView'
    const rootView: SapUi5RootView = {
        viewName: FIORI_FCL_ROOT_VIEW_NAME,
        type: ViewTypes.XML,
        async: true,
        id: FIORI_FCL_ROOT_ID
    };
    manifestUI5.rootView = manifestUI5.rootView ? { ...manifestUI5.rootView, ...rootView } : rootView;
    // Added in scope of #523. Starting from SAPUI5 1.83+ - we need make sure that dependency to `sap.f` library is added to manifest.
    updateDependencyLib(manifestUI5, v4.SAPUI5_DEPENDENCY_LIB_SAP_F);
};

/**
 * Method disables FCL support by updating Manifest.
 *
 * @param sapui5 - SAPUI5 manifest data.
 */
const disableFcl = (sapui5: v4.SapUi5V4): void => {
    if (sapui5.rootView && sapui5.rootView.viewName === FIORI_FCL_ROOT_VIEW_NAME) {
        // FCL is disabled and rootview name is equal to FCL ROOT VIEW, then remove rootview
        delete sapui5.rootView;
    }
    if (sapui5.routing?.config && sapui5.routing.config.routerClass === v4.FIORI_FCL_ROUTER_CLASS) {
        // Remove routing class
        delete sapui5.routing.config.routerClass;
    }
};

/**
 * Method updates Manifest depending on is FCL ON or OFF.
 *
 * @param manifestUI5 - SAPUI5 manifest data.
 * @param app - Project application.
 */
export const updateFcl = (manifestUI5: v4.SapUi5V4, app: ApplicationV4): void => {
    if (app.settings && app.settings.flexibleColumnLayout) {
        enableFcl(manifestUI5, app);
    } else {
        disableFcl(manifestUI5);
    }
};
